import type { Destination } from "../data";

interface Props {
  dest: Destination;
  index: number;
}

export default function DestinationSection({ dest, index }: Props) {
  const reversed = index % 2 === 1;

  return (
    <section id={dest.id} className="w-full py-12 md:py-16 bg-background-50 scroll-mt-20">
      <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-accent-600">
              <i className="ri-map-pin-2-line"></i> {dest.region} · {dest.state}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
              {dest.name}{" "}
              <span className="italic text-accent-600">— {dest.tagline}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-accent-100 text-accent-900 text-sm font-semibold px-4 py-2 whitespace-nowrap">
              Est. ROI {dest.roi}
            </span>
            <span className="rounded-full bg-secondary-100 text-secondary-900 text-sm font-semibold px-4 py-2 whitespace-nowrap">
              From {dest.startPrice}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* Image + climate */}
          <div className={`space-y-5 ${reversed ? "lg:order-2" : ""}`}>
            <div className="relative rounded-3xl overflow-hidden h-[280px] md:h-[340px] w-full">
              <img
                src={dest.image}
                alt={`${dest.name} — ${dest.tagline}, ${dest.state}`}
                className="w-full h-full object-cover"
              />
              <span className="absolute top-4 left-4 rounded-full bg-background-50/90 backdrop-blur text-foreground-950 text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5">
                <i className="ri-sun-line text-accent-600"></i> {dest.focus}
              </span>
            </div>

            {/* Climate card */}
            <div className="rounded-2xl bg-background-100 border border-background-200 p-5">
              <h4 className="flex items-center gap-2 text-sm font-bold text-foreground-950 uppercase tracking-wide">
                <i className="ri-thermometer-line text-accent-600"></i> Climate & Weather
              </h4>
              <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{dest.climateSummary}</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {dest.climate.map((c) => (
                  <div key={c.label} className="rounded-xl bg-background-50 border border-background-200 px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide text-foreground-500">{c.label}</p>
                    <p className="text-sm font-semibold text-foreground-950 mt-0.5">{c.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detail panel */}
          <div className={`space-y-5 ${reversed ? "lg:order-1" : ""}`}>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{dest.summary}</p>

            {/* Highlights */}
            <div className="rounded-2xl bg-background-100 border border-background-200 p-5">
              <h4 className="flex items-center gap-2 text-sm font-bold text-foreground-950 uppercase tracking-wide">
                <i className="ri-sparkling-2-line text-accent-600"></i> Place Highlights
              </h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {dest.highlights.map((h) => (
                  <span
                    key={h}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-800 bg-background-50 border border-background-300 rounded-full px-3 py-1.5"
                  >
                    <i className="ri-check-line text-primary-600"></i> {h}
                  </span>
                ))}
              </div>
            </div>

            {/* Advantages */}
            <div className="rounded-2xl bg-primary-100/70 border border-primary-200 p-5">
              <h4 className="flex items-center gap-2 text-sm font-bold text-foreground-950 uppercase tracking-wide">
                <i className="ri-trophy-line text-primary-700"></i> Why This Destination
              </h4>
              <ul className="mt-3 space-y-2.5">
                {dest.advantages.map((a) => (
                  <li key={a} className="flex items-start gap-2.5 text-sm text-foreground-800">
                    <i className="ri-arrow-right-s-line text-primary-600 mt-0.5"></i>
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Investment advantages */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {dest.investment.map((block) => (
            <div
              key={block.title}
              className="rounded-2xl bg-background-100 border border-background-200 p-5 hover:border-accent-300 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-accent-100 text-accent-700">
                <i className="ri-line-chart-line text-lg"></i>
              </span>
              <h5 className="font-heading text-base text-foreground-950 mt-3">{block.title}</h5>
              <ul className="mt-2.5 space-y-2">
                {block.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-foreground-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-1.5 shrink-0"></span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}